/* global analytics */
import { Component, ComponentType, RefObject, createRef } from 'react'
import isEqual from 'react-fast-compare'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import has from 'lodash/has'

import GoogleMapReact from 'google-map-react'
import isBot from 'isbot'

import useDispatch from 'hooks/useDispatch'
import { mediaMax } from 'styles/media'
import { waitForGoogleMaps } from '../../../utils/googleMapsLoader'
import DescendantOfMapContext from '../../../contexts/DescendantOfMapContext'
import { updateFilters } from '../../../actions/searchActions'
import useIsServer from '../../../hooks/useIsServer'
import { useGetSelectEscapeIndexesQuery } from '../../../hooks/useQueries'

import Button from '../../display/Button'
import InlineSpinner from '../../display/InlineSpinner'
import IconPlus from '../../icons/PlusThin'
import IconMinus from '../../icons/MinusThin'
import { Marker } from '../ListingMap/markers'

import LatLng from './latLng'
import { Bounds, generateSpiderParams } from './mapUtils'

const MapWrapper = styled.div`
  overflow: hidden;
  width: 100%;
  height: 100%;
  background-color: #e1dfda;
  position: relative;
`

const LoadingOverlay = styled.div<{ show?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  background-color: black;
  opacity: 0;
  z-index: -1;
  transition: z-index 500ms ease-out, opacity 500ms ease-out;
  ${({ show }) =>
    show &&
    `
    z-index: 2;
    opacity: 0.4;
  `}
`

const ReflectMapResults = styled(Button)`
  position: absolute;
  top: 16px;
  right: 0;
  left: 0;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 160px;
  border-radius: 100px;

  font-size: 14px;
  text-transform: capitalize;
  line-height: 1;
  letter-spacing: 0.25px;

  color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  outline: 0;
  background-color: white;

  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    color: ${({ theme }) => theme?.['-ds-core-400']?.value};
  }
`

const CustomZoom = styled.div`
  position: absolute;
  z-index: 8;
  top: 50px;
  right: 0;
  margin: 10px;
  background-color: ${({ theme }) => theme?.['-ds-neutral-100']?.value};
  color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  border-radius: 2px;
  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  padding: 8px;

  ${mediaMax.md`
    display: none;
  `}

  > button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: transparent;
    border: none;
    box-shadow: none;

    &:hover:not(:disabled) {
      color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
    }

    &:disabled {
      color: ${({ theme }) => theme?.['-ds-neutral-400']?.value};
    }
  }

  svg {
    width: 24px;
    height: auto;
  }

  hr {
    margin-top: 8px;
    margin-bottom: 8px;
    border-color: ${({ theme }) => theme?.['-ds-neutral-600']?.value};
  }
`

const resultIsAccommodation = (mapResultType: GQL_MapResult['type']) => {
  switch (mapResultType) {
    case 'Adventure':
      return false
    case 'Listing':
    default:
      return true
  }
}

const panMapYByPx = () => {
  return 180 // approximately half of the typical popup height (which varies slightly, but simpler to keep this offset static for now)
}

const shouldShowSearchThisArea = ({
  dragging,
  hasBeenZoomed,
  hasBeenDragged,
  markerIsOpen,
}: {
  dragging: boolean
  hasBeenZoomed: boolean
  hasBeenDragged: boolean
  markerIsOpen: boolean
}) => {
  if (dragging) {
    return true
  }

  if (hasBeenZoomed) {
    return true
  }

  if (markerIsOpen && !dragging && !hasBeenDragged) {
    return false
  }

  return hasBeenDragged
}

interface Cluster {
  id?: number
  geometry: {
    coordinates: number[]
    type: string
  }
  properties: {
    cluster_id?: number
    point_count?: number
    id: number | string
    slug: string
    type: GQL_Searchable
    adventure_type?: {
      slug: string
    }
    cluster?: any
    notBucketListable?: boolean
    disablePopup?: boolean
  }
}

export type Result = Omit<
  GQL_MapResult,
  'coordinates' | 'markerCount' | '__typename'
> & {
  location: number[]
  notBucketListable?: boolean // e.g. on-property adventures cannot be added to bucket list
  disablePopup?: boolean
}

const DEFAULT_CENTER = [-37.814, 144.96332] // Melbourne
const DEFAULT_ZOOM = 14

const baseOptions = {
  maxZoom: 14,
  gestureHandling: 'greedy',
  zoomControl: false,
  cameraControl: false,
  clickableIcons: false,
}

type SearchMapBaseProps = {
  results: Result[]
  unclusteredResults?: Result[] // to be rendered as separate markers at all zoom levels, that won't cluster with others
  loading?: boolean
  filters?: GQL_SearchFilters
  searchQuery?: string
  defaultCenter?: [number, number]
  defaultZoom?: number
  noSearchThisArea?: boolean
  mobile?: boolean
  analyticsSource: string // analytics events property
  PopupComponent: ComponentType<any>
}

type InjectedProps = {
  dispatch: (message: any) => void
  serverSide: boolean
  bookingAdventuresIndex: GQL_BookingAdventuresIndex
  bucketListItemsIndex: GQL_BucketListItemsIndex
}

type MapFullProps = SearchMapBaseProps & InjectedProps

type MapState = {
  map?: any // Current Google Map Instance
  maps?: any // Google Maps JS API
  center: number[]
  zoom?: number
  bounds: Bounds
  makingClusters: boolean
  dragging: boolean
  hasBeenDragged: boolean
  hasBeenZoomed: boolean
  openedMarkerId?: number | string
  openedClusterId?: number | string
  clusters: Cluster[]
  leaves: Cluster[]
  fitToBounds: boolean
}

class Map extends Component<MapFullProps, MapState> {
  fitBoundsToResultsDebounced: () => any

  mapContainer: RefObject<HTMLDivElement>

  ClusteringWorker: any

  static defaultProps: {
    defaultZoom: number
  }

  constructor(props: MapFullProps) {
    super(props)
    this.state = {
      map: null, // Current Google Map Instance
      maps: null, // Google Maps JS API
      center: props.defaultCenter || DEFAULT_CENTER,
      zoom: props.defaultZoom || DEFAULT_ZOOM,
      bounds: {
        nw: new LatLng(85, -180),
        ne: new LatLng(85, 180),
        sw: new LatLng(-85, -180),
        se: new LatLng(-85, 180),
      },
      openedMarkerId: null,
      openedClusterId: null,
      clusters: [],
      leaves: [],
      makingClusters: false,
      dragging: false,
      hasBeenDragged: false,
      hasBeenZoomed: false,
      fitToBounds: false,
    }

    this.fitBoundsToResultsDebounced = debounce(this.fitBoundsToResults, 300)
    this.mapContainer = createRef()
  }

  /**
   * We need to initialize the clustering web worker
   */
  componentDidMount() {
    // eslint-disable-next-line global-require
    const ClusteringWorker = require('./clustering-gql.worker')
    // @ts-ignore -- web worker interface needs typing
    this.ClusteringWorker = new ClusteringWorker()
    this.ClusteringWorker.addEventListener(
      'message',
      ({ data }: { data: MapState }) => {
        requestAnimationFrame(() => {
          this.setState(data)
        })
      }
    )
  }

  componentDidUpdate(prevProps: MapFullProps) {
    const { results } = this.props

    const noNewResults = isEqual(prevProps.results, results)

    if (noNewResults !== true) {
      /**
       * We need to fit the bounds of the map to the listings we received
       * and initialize our clusters, but only when searchMapResults have been
       * fetched or refetched
       */
      this.fitBoundsToResultsDebounced()
      /**
       * We need to wait 500ms for the fitBounds animation to finish before
       * displaying our markers for the first time
       */
      setTimeout(() => {
        this.makeClusters()
      }, 600)
    }
  }

  componentWillUnmount() {
    this.ClusteringWorker.terminate()
  }

  /**
   * We need to build up clusters with the listings results coming in from the API
   */
  makeClusters = () => {
    const { results } = this.props

    this.setState({
      makingClusters: true,
    })

    /**
     * We need to be careful not to send `map` or `maps` objects
     * to the worker as this causes a JS error
     */
    const { map, maps, ...state } = this.state

    this.ClusteringWorker.postMessage({
      type: 'makeClusters',
      params: {
        results,
        ...state,
      },
    })
  }

  /**
   * We need to fit the bounds of the map to the results
   * we receive from the API
   */
  fitBoundsToResults = () => {
    const {
      results,
      noSearchThisArea,
      filters,
      unclusteredResults,
    } = this.props

    // If filtered by country/state or has prop noSearchThisArea
    if (noSearchThisArea || filters.country || filters.state) {
      const { map, maps, zoom } = this.state

      const combinedResults = [...results, ...(unclusteredResults || [])]

      if (!map || !maps || combinedResults.length === 0) {
        return
      }

      const bounds = new maps.LatLngBounds()
      combinedResults.forEach((result) => {
        const [lat, lng] = result.location
        const latLng = new maps.LatLng(lat, lng)
        bounds.extend(latLng)
      })

      this.setState({
        fitToBounds: true,
        hasBeenZoomed: false,
        hasBeenDragged: false,
      })

      map.fitBounds(bounds, 40)

      if (map.getZoom() === zoom) {
        this.setState({
          fitToBounds: false,
        })
      }
    }
  }

  /**
   * We need to send the map bounds as filters so they appear in the
   * URL parameters and update the listing results
   */
  handleReflectMapResults = () => {
    const { dispatch } = this.props
    const { bounds } = this.state

    dispatch(
      updateFilters({
        sw: [bounds.sw.lat, bounds.sw.lng],
        ne: [bounds.ne.lat, bounds.ne.lng],
        location: null,
        origin: null,
        country: null,
      })
    )
    analytics.track('Search by Map')

    this.setState({
      hasBeenZoomed: false,
      hasBeenDragged: false,
    })
  }

  handleClusterClick = ({ geometry, properties }: Cluster) => {
    const { zoom, openedClusterId } = this.state
    const clusterId = properties.cluster_id
    /**
     * We need to close a cluster if it is already opened
     */
    if (clusterId === openedClusterId) {
      this.setState({
        openedClusterId: null,
        leaves: [],
      })

      /**
       * We need to zoom and center to the next level of cluster extension
       * or expand the cluster to show its leaves if at MAX_ZOOM
       */
    } else {
      this.ClusteringWorker.postMessage({
        type: 'zoomUntilClusterExpands',
        params: {
          clusterId,
          center: geometry.coordinates.slice().reverse(),
          zoom,
        },
      })
    }
  }

  /**
   * We need to open a marker callout and fetch the data for display
   * when clicking on a marker
   */
  handleMarkerClick = (
    resultId: string | number,
    center: MapState['center'],
    disablePopup?: boolean
  ) => {
    const { map, openedMarkerId } = this.state

    this.setState(
      {
        center,
        openedMarkerId: disablePopup ? null : resultId,
        hasBeenDragged: false,
      },
      () => {
        if (openedMarkerId !== resultId && !disablePopup) {
          const panDownBy = panMapYByPx()
          map.panTo({ lat: center[0], lng: center[1] })
          map.panBy(0, -panDownBy)
        } else {
          this.handleCalloutClose()
        }
      }
    )
  }

  /**
   * Same but for a Leaf
   */
  handleLeafClick = (
    resultId: string | number,
    geometry: Cluster['geometry'],
    leafParams: { x: number; y: number },
    disablePopup?: boolean
  ) => {
    const { map, openedMarkerId } = this.state
    const leafParentCoordinates = geometry.coordinates.slice().reverse() as [
      number,
      number
    ]
    const panDownBy = panMapYByPx()

    this.setState(
      {
        center: leafParentCoordinates,
        openedMarkerId: disablePopup ? null : resultId,
        hasBeenDragged: false,
      },
      () => {
        if (openedMarkerId !== resultId) {
          // NB: panning is in pixels, not geo coordinates
          const { x: leafXOffset, y: leafYOffset } = leafParams
          map.panTo({
            lat: leafParentCoordinates[0],
            lng: leafParentCoordinates[1],
          })
          map.panBy(0 + leafXOffset, -panDownBy + leafYOffset)
        }
      }
    )
  }

  handleCalloutClose = () => {
    this.setState({
      openedMarkerId: null,
    })
  }

  handleGoogleApiLoaded = ({ map, maps }: { map: any; maps: any }) => {
    this.setState({
      map,
      maps,
    })
    this.fitBoundsToResultsDebounced()
  }

  /**
   * We need to make sure clusters are regenerated everytime we move
   * zoom or pan the map
   */
  handleOnChange = ({
    center,
    zoom,
    bounds,
  }: {
    center: [number, number]
    zoom: number
    bounds: Bounds
  }) => {
    this.setState(
      {
        center,
        zoom,
        bounds,
      },
      () => {
        this.makeClusters()
      }
    )
  }

  suspendGestureHandling = () => {
    const { map } = this.state
    map.setOptions({
      gestureHandling: 'none',
    })
  }

  restoreGestureHandling = () => {
    const { map } = this.state

    map.setOptions({
      gestureHandling: baseOptions.gestureHandling,
    })
  }

  createMapOptions = () => {
    const { mobile } = this.props

    if (mobile) {
      return {
        ...baseOptions,
        fullscreenControl: false,
      }
    }

    return {
      ...baseOptions,
    }
  }

  handleZoomClick = (newZoom: number) => {
    const { map } = this.state
    map.setZoom(newZoom)
  }

  handleZoomStart = (nextZoom: number) => {
    const { fitToBounds, zoom: prevZoom } = this.state

    if (nextZoom !== prevZoom) {
      if (fitToBounds) {
        this.setState({
          fitToBounds: false,
        })
      } else {
        this.setState({
          hasBeenZoomed: true,
        })
      }
    }
  }

  renderCluster = (c: Cluster) => {
    const hasAccommodationResults = has(c.properties, 'Listing')

    return (
      <Marker
        key={`cluster-${c.id}`}
        // @ts-ignore -- Marker not typed yet
        lng={c.geometry.coordinates[0]}
        lat={c.geometry.coordinates[1]}
        onClick={() => this.handleClusterClick(c)}
        jungle={hasAccommodationResults}
      >
        <span>{c.properties.point_count}</span>
      </Marker>
    )
  }

  renderMarker = (c: Cluster) => {
    const {
      bookingAdventuresIndex,
      bucketListItemsIndex,
      filters,
      analyticsSource,
      PopupComponent,
    } = this.props
    const { openedMarkerId } = this.state
    const { id, type, slug, disablePopup } = c.properties

    return (
      <PopupComponent
        key={`marker-${id}`}
        type={type}
        // @ts-ignore -- not typed yet
        filters={filters}
        id={id}
        lng={c.geometry.coordinates[0]}
        lat={c.geometry.coordinates[1]}
        onClose={this.handleCalloutClose}
        open={id === openedMarkerId}
        bookingAdventuresIndex={bookingAdventuresIndex}
        bucketListItemsIndex={bucketListItemsIndex}
        onPopupInteractionStart={this.suspendGestureHandling}
        onPopupInteractionEnd={this.restoreGestureHandling}
        mapContainerRef={this.mapContainer}
        source={analyticsSource}
        {...c.properties} // pass any additional props to popup that it might need -- the Map component shouldn't have an opinion on this
      >
        <Marker
          onClick={() =>
            this.handleMarkerClick(
              id,
              c.geometry.coordinates.slice().reverse(),
              disablePopup
            )
          }
          // @ts-ignore -- not typed yet
          jungle={resultIsAccommodation(type)}
        >
          <i className={`icon icon-${slug}`} />
        </Marker>
      </PopupComponent>
    )
  }

  renderLeaves = (leaves: Cluster[]) => {
    const {
      bookingAdventuresIndex,
      bucketListItemsIndex,
      filters,
      analyticsSource,
      PopupComponent,
    } = this.props
    const { openedMarkerId } = this.state
    const params = generateSpiderParams(leaves.length)
    return leaves.map((c, i) => {
      const leafParams = params[i]
      const { id, type, slug, disablePopup } = c.properties
      return (
        <PopupComponent
          key={`marker-${c.properties.id}`}
          type={type}
          // @ts-ignore -- not typed yet
          filters={filters}
          id={c.properties.id}
          lng={c.geometry.coordinates[0]}
          lat={c.geometry.coordinates[1]}
          onClose={this.handleCalloutClose}
          open={c.properties.id === openedMarkerId}
          transform={leafParams}
          bookingAdventuresIndex={bookingAdventuresIndex}
          bucketListItemsIndex={bucketListItemsIndex}
          onPopupInteractionStart={this.suspendGestureHandling}
          onPopupInteractionEnd={this.restoreGestureHandling}
          mapContainerRef={this.mapContainer}
          source={analyticsSource}
          {...c.properties} // pass any additional props to popup that it might need -- the Map component shouldn't have an opinion on this
        >
          <Marker
            onClick={() =>
              this.handleLeafClick(id, c.geometry, leafParams, disablePopup)
            }
            // @ts-ignore -- not typed yet
            jungle={resultIsAccommodation(c.properties.type)}
          >
            <i className={`icon icon-${slug}`} />
          </Marker>
        </PopupComponent>
      )
    })
  }

  renderClusters = (clusters: Cluster[]) => {
    return clusters.map((c) =>
      c.properties.cluster ? this.renderCluster(c) : this.renderMarker(c)
    )
  }

  renderUnclusteredResults = (results: MapFullProps['unclusteredResults']) => {
    return results.map((result) => {
      const { id, type, slug, disablePopup, location } = result
      return this.renderMarker({
        properties: {
          id,
          slug,
          type,
          disablePopup,
          ...result, // pass through additional properties that the popup component might need
        },
        geometry: {
          type: 'Point',
          coordinates: location.slice().reverse(), // [lat, lng] => [lng, lat]
        },
      })
    })
  }

  render() {
    if (isBot(navigator.userAgent)) return null

    const {
      unclusteredResults,
      serverSide,
      loading,
      noSearchThisArea,
    } = this.props
    const {
      bounds,
      center,
      clusters,
      dragging,
      hasBeenDragged,
      leaves,
      makingClusters,
      openedMarkerId,
      zoom,
      hasBeenZoomed,
      map,
    } = this.state

    const readying = loading || makingClusters

    const showSearchThisArea =
      !noSearchThisArea &&
      shouldShowSearchThisArea({
        markerIsOpen: !!openedMarkerId,
        hasBeenDragged,
        dragging,
        hasBeenZoomed,
      })

    return (
      // eslint-disable-next-line react/jsx-boolean-value -- deliberately explicit about all descendants being descendants of a map
      <DescendantOfMapContext.Provider value={true}>
        <MapWrapper ref={this.mapContainer}>
          {!serverSide && (
            // @ts-ignore -- types around the coordinates data structure need to be fixed
            <GoogleMapReact
              googleMapLoader={waitForGoogleMaps}
              center={center}
              zoom={zoom}
              bounds={bounds}
              options={this.createMapOptions}
              onChange={this.handleOnChange}
              onDrag={() => {
                this.setState({ dragging: true, hasBeenDragged: true })
              }}
              onDragEnd={() => this.setState({ dragging: false })}
              onZoomAnimationStart={this.handleZoomStart}
              yesIWantToUseGoogleMapApiInternals
              onGoogleApiLoaded={this.handleGoogleApiLoaded}
            >
              {!loading && this.renderClusters(clusters)}
              {!loading && this.renderLeaves(leaves)}
              {!loading &&
                unclusteredResults?.length > 0 &&
                this.renderUnclusteredResults(unclusteredResults)}
            </GoogleMapReact>
          )}
          {showSearchThisArea && (
            <ReflectMapResults onClick={this.handleReflectMapResults}>
              {readying ? <InlineSpinner /> : `Search this area`}
            </ReflectMapResults>
          )}
          <LoadingOverlay show={readying} />

          {!loading && (
            <CustomZoom>
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => this.handleZoomClick(zoom + 1)}
                disabled={zoom === map?.maxZoom}
              >
                <IconPlus />
              </button>
              <hr />
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => this.handleZoomClick(zoom - 1)}
                disabled={zoom === map?.minZoom}
              >
                <IconMinus />
              </button>
            </CustomZoom>
          )}
        </MapWrapper>
      </DescendantOfMapContext.Provider>
    )
  }
}

Map.defaultProps = {
  defaultZoom: 7,
}

const withBucketListableProps = (MapComponent: typeof Map) => (
  props: SearchMapBaseProps
) => {
  const dispatch = useDispatch()
  const serverSide = useIsServer()

  const { data } = useGetSelectEscapeIndexesQuery()

  const bucketListItemsIndex = data?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex

  const bookingAdventuresIndex = data?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex

  if (serverSide) {
    return null
  }

  return (
    <MapComponent
      {...props}
      dispatch={dispatch}
      serverSide={serverSide}
      bookingAdventuresIndex={bookingAdventuresIndex}
      bucketListItemsIndex={bucketListItemsIndex}
    />
  )
}

export default withBucketListableProps(Map)
